import { yupResolver } from '@hookform/resolvers/yup';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { useFacebookSdk } from '../hooks/useFacebookSdk';
import { apiService } from '../services/api';
import { utilService } from '../services/util';

const emailField = yup
  .string()
  .email('Please enter a valid email address')
  .test('email', 'Please enter a valid email address', (value) => utilService.isValidEmail( value ?? ''))
  .required('Email is required');

const loginSchema = yup.object({
  email: emailField,
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
});

type LoginFormData = yup.InferType<typeof loginSchema>;

const resetEmailSchema = yup.object({
  email: emailField,
});

type ResetEmailFormData = yup.InferType<typeof resetEmailSchema>;

const resetPasswordSchema = yup.object({
  newPassword: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('New password is required'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

type ResetPasswordFormData = yup.InferType<typeof resetPasswordSchema>;

type PageMode =
  | 'login'
  | 'reset_email'
  | 'reset_code'
  | 'reset_new_password'
  | 'reset_success';

function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as { response?: { data?: { message?: string } } };
  return err.response?.data?.message ?? fallback;
}

const OTP_LEN = 6;
const emptyOtp = (): string[] => Array(OTP_LEN).fill('');

type SixDigitCodeInputProps = {
  digits: string[];
  onDigitsChange: (next: string[]) => void;
  disabled?: boolean;
  onPasteError: (message: string) => void;
};

const SixDigitCodeInput: React.FC<SixDigitCodeInputProps> = ({
  digits,
  onDigitsChange,
  disabled,
  onPasteError,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const setDigitAt = (index: number, raw: string) => {
    const d = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = d;
    onDigitsChange(next);
    if (d && index < OTP_LEN - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const only = pasted.replace(/\D/g, '');
    if (only.length !== OTP_LEN) {
      onPasteError('Paste a 6-digit code.');
      return;
    }
    onPasteError('');
    const next = only.split('');
    onDigitsChange(next);
    inputsRef.current[OTP_LEN - 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3" role="group" aria-label="6-digit verification code">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={1}
          disabled={disabled}
          aria-label={`Digit ${i + 1} of ${OTP_LEN}`}
          value={digit}
          onChange={(e) => setDigitAt(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className="w-11 h-14 sm:w-14 sm:h-16 text-center text-3xl sm:text-4xl font-semibold font-mono tabular-nums tracking-widest rounded-lg border border-secondary-300 bg-white text-secondary-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
        />
      ))}
    </div>
  );
};

const hasGoogleClientId = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);
const facebookAppId =
  process.env.REACT_APP_FACEBOOK_APP_ID ?? process.env.REACT_APP_FACEBOOK_CLIENT_ID ?? '';
const hasFacebookAppId = Boolean(facebookAppId);

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const { login, loginWithGoogle, loginWithFacebook } = useAuth();
  const navigate = useNavigate();
  const { isReady: facebookSdkReady } = useFacebookSdk(hasFacebookAppId ? facebookAppId : undefined);

  const [pageMode, setPageMode] = useState<PageMode>('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetBearerToken, setResetBearerToken] = useState('');
  const [resetFlowError, setResetFlowError] = useState('');
  const [resetRequestInfo, setResetRequestInfo] = useState<{ message: string; expiresAt?: string } | null>(
    null
  );
  const [otpDigits, setOtpDigits] = useState<string[]>(() => emptyOtp());
  const [requestResetLoading, setRequestResetLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resetPasswordSubmitLoading, setResetPasswordSubmitLoading] = useState(false);
  const otpVerifyInFlight = useRef(false);
  /** Same 6-digit string that already failed validation; cleared when the user edits the code. */
  const failedOtpRef = useRef<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  const resetEmailForm = useForm<ResetEmailFormData>({
    resolver: yupResolver(resetEmailSchema),
    defaultValues: { email: '' },
  });

  const resetPasswordForm = useForm<ResetPasswordFormData>({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const goToLogin = useCallback(() => {
    setPageMode('login');
    setResetEmail('');
    setResetBearerToken('');
    setResetFlowError('');
    setResetRequestInfo(null);
    setOtpDigits(emptyOtp());
    failedOtpRef.current = '';
    resetPasswordForm.reset();
    resetEmailForm.reset();
  }, [resetEmailForm, resetPasswordForm]);

  const goToForgotEmail = () => {
    setResetFlowError('');
    setResetRequestInfo(null);
    setPageMode('reset_email');
    resetEmailForm.setValue('email', '');
  };

  const goBackToResetEmail = () => {
    setResetFlowError('');
    setOtpDigits(emptyOtp());
    failedOtpRef.current = '';
    setPageMode('reset_email');
    resetEmailForm.setValue('email', resetEmail);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password);
      navigate('/dashboard');
    } catch {
      // Error is handled in the auth context
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitResetEmail = async (data: ResetEmailFormData) => {
    setResetFlowError('');
    setRequestResetLoading(true);
    try {
      const res = await apiService.requestPasswordReset(data.email);
      setResetEmail(data.email.trim());
      setResetRequestInfo({ message: res.message, expiresAt: res.expiresAt });
      setOtpDigits(emptyOtp());
      failedOtpRef.current = '';
      setPageMode('reset_code');
    } catch (error) {
      setResetFlowError(getApiErrorMessage(error, 'Could not send reset code.'));
    } finally {
      setRequestResetLoading(false);
    }
  };

  const otpCode = otpDigits.join('');

  useEffect(() => {
    if (pageMode !== 'reset_code' || otpCode.length !== OTP_LEN || !resetEmail) {
      return;
    }
    if (otpCode === failedOtpRef.current) {
      return;
    }
    if (otpVerifyInFlight.current) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      otpVerifyInFlight.current = true;
      setOtpLoading(true);
      setResetFlowError('');
      try {
        const validationResponse = await apiService.validateResetToken(resetEmail, otpCode);
        if (cancelled) return;
        const bearerToken = validationResponse.token.value;
        if (!bearerToken) {
          throw new Error('Missing reset bearer token from validation response.');
        }
        failedOtpRef.current = '';
        setResetBearerToken(bearerToken);
        setResetEmail('');
        setOtpDigits(emptyOtp());
        setResetRequestInfo(null);
        setPageMode('reset_new_password');
        resetPasswordForm.reset();
      } catch (error) {
        if (cancelled) return;
        failedOtpRef.current = otpCode;
        setResetFlowError(getApiErrorMessage(error, 'Invalid or expired code.'));
      } finally {
        if (!cancelled) {
          otpVerifyInFlight.current = false;
          setOtpLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [pageMode, otpCode, resetEmail, resetPasswordForm]);

  const handleOtpDigitsChange = (next: string[]) => {
    setOtpDigits(next);
    setResetFlowError('');
    failedOtpRef.current = '';
  };

  const handleOtpPasteError = (msg: string) => {
    if (msg) {
      setResetFlowError(msg);
    } else {
      setResetFlowError('');
    }
  };

  const onSubmitNewPassword = async (data: ResetPasswordFormData) => {
    if (!resetBearerToken) {
      setResetFlowError('Your reset session has expired. Please request a new code.');
      setPageMode('reset_email');
      return;
    }
    setResetFlowError('');
    setResetPasswordSubmitLoading(true);
    try {
      await apiService.resetPassword(resetBearerToken, data.newPassword, data.confirmPassword);
      setPageMode('reset_success');
    } catch (error) {
      setResetFlowError(getApiErrorMessage(error, 'Could not reset password.'));
    } finally {
      setResetPasswordSubmitLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    const credential = credentialResponse.credential;
    if (!credential) return;
    setGoogleLoading(true);
    try {
      await loginWithGoogle(credential);
      navigate('/dashboard');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleFacebookClick = () => {
    if (!window.FB || !facebookSdkReady) return;
    setFacebookLoading(true);
    window.FB.login(
      (response) => {
        if (response.status !== 'connected' || !response.authResponse) {
          setFacebookLoading(false);
          if (response.status === 'unknown') {
            toast.error('Sign in with Facebook was cancelled.');
          } else {
            toast.error('Sign in with Facebook failed. Please try again.');
          }
          return;
        }
        const { accessToken, userID } = response.authResponse;
        window.FB!.api(
          '/me',
          { fields: 'id,name,email' },
          (userOrError: { id?: string; name?: string; email?: string; error?: unknown }) => {
            if (userOrError?.error || !userOrError?.id) {
              setFacebookLoading(false);
              toast.error('Could not load your Facebook profile. Please try again.');
              return;
            }
            loginWithFacebook({
              accessToken,
              providerId: userID,
              email: userOrError.email,
              name: userOrError.name,
            })
              .then(() => navigate('/dashboard'))
              .catch(() => {
                /* AuthContext already shows toast */
              })
              .finally(() => setFacebookLoading(false));
          }
        );
      },
      { scope: 'email,public_profile' }
    );
  };

  const showLoginChrome = pageMode === 'login';

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-secondary-900">
            {pageMode === 'login' && 'Sign in to your account'}
            {pageMode === 'reset_email' && 'Reset your password'}
            {pageMode === 'reset_code' && 'Enter verification code'}
            {pageMode === 'reset_new_password' && 'Choose a new password'}
            {pageMode === 'reset_success' && 'Password updated'}
          </h2>
          {showLoginChrome && (
            <p className="mt-2 text-center text-sm text-secondary-600">
              Or{' '}
              <Link
                to="/register"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                create a new account
              </Link>
            </p>
          )}
          {pageMode === 'reset_email' && (
            <p className="mt-2 text-center text-sm text-secondary-600">
              Enter your account email and we will send a 6-digit code.
            </p>
          )}
          {pageMode === 'reset_code' && (
            <p className="mt-2 text-center text-sm text-secondary-600">
              We sent a code to{' '}
              <span className="font-medium text-secondary-800">{resetEmail}</span>
            </p>
          )}
          {pageMode === 'reset_new_password' && (
            <p className="mt-2 text-center text-sm text-secondary-600">
              Code verified. Enter and confirm your new password.
            </p>
          )}
          {pageMode === 'reset_success' && (
            <p className="mt-2 text-center text-sm text-secondary-600">
              You can now sign in with your new password.
            </p>
          )}
        </div>

        {showLoginChrome && (
          <>
            <div className="mt-8 space-y-3 relative">
              {(googleLoading || facebookLoading) && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-secondary-50/90"
                  aria-busy="true"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
                    <span className="text-sm font-medium text-secondary-700">
                      {googleLoading ? 'Signing in with Google…' : 'Signing in with Facebook…'}
                    </span>
                  </div>
                </div>
              )}
              {hasGoogleClientId ? (
                <div className="flex justify-center [&>div]:!w-full [&>div]:!flex [&>div]:!justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => {
                      setGoogleLoading(false);
                    }}
                    useOneTap={false}
                    theme="filled_black"
                    size="large"
                    text="signin_with"
                    width={320}
                    containerProps={{ className: 'w-full flex justify-center' }}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  disabled
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-500 bg-secondary-100 cursor-not-allowed"
                  title="Configure REACT_APP_GOOGLE_CLIENT_ID to enable"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google (not configured)
                </button>
              )}

              {hasFacebookAppId && false && (
                facebookSdkReady ? (
                  <button
                    type="button"
                    onClick={handleFacebookClick}
                    disabled={facebookLoading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#1877F2" aria-hidden>
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    Sign in with Facebook
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-500 bg-secondary-100 cursor-wait"
                    title="Loading Facebook SDK…"
                  >
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-secondary-400 border-t-transparent" />
                    Sign in with Facebook (loading…)
                  </button>
                )
              )}
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center" aria-hidden>
                <div className="w-full border-t border-secondary-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-secondary-50 text-secondary-600">Or continue with email</span>
              </div>
            </div>
          </>
        )}

        {pageMode === 'login' && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-secondary-700">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    {...register('email')}
                    type="email"
                    autoComplete="email"
                    className="input-field pl-10"
                    placeholder="Enter your email"
                    pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}"
                  />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-secondary-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={goToForgotEmail}
                    className="text-sm font-medium text-primary-600 hover:text-primary-500"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="input-field pl-10 pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-secondary-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-secondary-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        )}

        {pageMode === 'reset_email' && (
          <form className="mt-8 space-y-6" onSubmit={resetEmailForm.handleSubmit(onSubmitResetEmail)}>
            {resetFlowError && (
              <p className="text-sm text-red-600" role="alert">
                {resetFlowError}
              </p>
            )}
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-secondary-700">
                Email address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  {...resetEmailForm.register('email')}
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  className="input-field pl-10"
                  placeholder="Enter your email"
                  pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}"
                />
              </div>
              {resetEmailForm.formState.errors.email && (
                <p className="mt-1 text-sm text-red-600">{resetEmailForm.formState.errors.email.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={requestResetLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {requestResetLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                'Send reset code'
              )}
            </button>
            <div className="text-center">
              <button type="button" onClick={goToLogin} className="text-sm font-medium text-primary-600 hover:text-primary-500">
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {pageMode === 'reset_code' && (
          <div className="mt-8 space-y-6">
            {resetRequestInfo && (
              <p className="text-sm text-center text-secondary-700 bg-secondary-100 rounded-lg py-2 px-3">
                {resetRequestInfo.message}
                {resetRequestInfo.expiresAt && (
                  <span className="block mt-1 text-xs text-secondary-500">
                    Code expires {new Date(resetRequestInfo.expiresAt).toLocaleString()}
                  </span>
                )}
              </p>
            )}
            {resetFlowError && (
              <p className="text-sm text-red-600 text-center" role="alert">
                {resetFlowError}
              </p>
            )}
            <SixDigitCodeInput
              digits={otpDigits}
              onDigitsChange={handleOtpDigitsChange}
              disabled={otpLoading}
              onPasteError={handleOtpPasteError}
            />
            {otpLoading && (
              <p className="text-center text-sm text-secondary-600">Verifying code…</p>
            )}
            <div className="flex flex-col gap-2 text-center">
              <button
                type="button"
                onClick={goBackToResetEmail}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Use a different email
              </button>
              <button type="button" onClick={goToLogin} className="text-sm font-medium text-secondary-600 hover:text-secondary-500">
                Back to sign in
              </button>
            </div>
          </div>
        )}

        {pageMode === 'reset_new_password' && (
          <form className="mt-8 space-y-6" onSubmit={resetPasswordForm.handleSubmit(onSubmitNewPassword)}>
            {resetFlowError && (
              <p className="text-sm text-red-600" role="alert">
                {resetFlowError}
              </p>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-secondary-700">
                  New password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    {...resetPasswordForm.register('newPassword')}
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pl-10 pr-10"
                    placeholder="New password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5 text-secondary-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-secondary-400" />
                    )}
                  </button>
                </div>
                {resetPasswordForm.formState.errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {resetPasswordForm.formState.errors.newPassword.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-secondary-700">
                  Confirm password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    {...resetPasswordForm.register('confirmPassword')}
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input-field pl-10 pr-10"
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-secondary-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-secondary-400" />
                    )}
                  </button>
                </div>
                {resetPasswordForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {resetPasswordForm.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={resetPasswordSubmitLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetPasswordSubmitLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                'Reset password'
              )}
            </button>
          </form>
        )}

        {pageMode === 'reset_success' && (
          <div className="mt-8 space-y-6 text-center">
            <p className="text-secondary-700">
              Your password was changed successfully. Please sign in with your new password.
            </p>
            <button
              type="button"
              onClick={goToLogin}
              className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
